import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as groupsService from '../services/groups';
import type { CreateGroupInput, UpdateGroupInput } from '../types/groups.types';

export const groupKeys = {
  all: ['groups'] as const,
  lists: () => [...groupKeys.all, 'list'] as const,
  list: (includeArchived: boolean) =>
    [...groupKeys.lists(), { includeArchived }] as const,
  detail: (groupId: string) => [...groupKeys.all, 'detail', groupId] as const,
};

export function useGroups(includeArchived = false) {
  return useQuery({
    queryKey: groupKeys.list(includeArchived),
    queryFn: () => groupsService.listGroups(includeArchived),
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateGroupInput) => groupsService.createGroup(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

export function useUpdateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateGroupInput) => groupsService.updateGroup(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

export function useGroup(groupId: string) {
  return useQuery({
    queryKey: groupKeys.detail(groupId),
    queryFn: () => groupsService.getGroup(groupId),
    staleTime: 2 * 60 * 1000,
    enabled: !!groupId,
  });
}

export function useArchiveGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) => groupsService.archiveGroup(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupKeys.all });
    },
  });
}

export function useRestoreGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) => groupsService.restoreGroup(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupKeys.all });
    },
  });
}
